import React, { Component } from 'react';
import { motion } from 'framer-motion';
import { WandSparklesIcon, MoonIcon, SunIcon, CloudIcon } from 'lucide-react';
import { useApp } from '../context/AppContext';
function ToggleSwitch({
  checked,
  onChange,
  label
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return <button type="button" role="switch" aria-checked={checked} aria-label={label} onClick={() => onChange(!checked)} className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${checked ? 'bg-navy-900' : 'bg-navy-100'}`} data-id="element-496">
      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-all ${checked ? 'left-[22px]' : 'left-0.5'}`} data-id="element-497" />
    </button>;
}
function SettingRow({
  icon: Icon,
  title,
  description,
  control
}: {
  icon: ComponentType<{
    className?: string;
  }>;
  title: string;
  description: string;
  control: React.ReactNode;
}) {
  return <div className="flex items-center gap-4 px-5 py-4" data-id="element-498">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-navy-50 text-navy-700" data-id="element-499">
        <Icon className="h-4 w-4" data-id="element-500" />
      </span>
      <div className="min-w-0 flex-1" data-id="element-501">
        <p className="text-sm font-medium text-navy-900" data-id="element-502">{title}</p>
        <p className="mt-0.5 text-xs leading-relaxed text-navy-400" data-id="element-503">
          {description}
        </p>
      </div>
      {control}
    </div>;
}
export function Settings() {
  const {
    showBuilderGuide,
    setShowBuilderGuide,
    theme,
    toggleTheme,
    driveSynced,
    setDriveSynced
  } = useApp();
  return <div className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6 lg:px-8" data-id="element-504">
      <h2 className="text-xl font-semibold tracking-tight text-navy-900" data-id="element-505">
        Settings
      </h2>
      <p className="mt-1 text-sm text-navy-500" data-id="element-506">
        Personal preferences for your reporting workspace.
      </p>

      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3,
      ease: 'easeOut'
    }} className="mt-6 space-y-6" data-id="element-507">
        <section aria-labelledby="settings-builder" data-id="element-508">
          <h3 id="settings-builder" className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-wider text-navy-400" data-id="element-509">
            Report Builder
          </h3>
          <div className="card-float divide-y divide-navy-50 rounded-2xl bg-white" data-id="element-510">
            <SettingRow icon={WandSparklesIcon} title="Guided start" description="Show the “Build it with me” guide when you open the Report Builder. When off, you'll go straight to a blank report." control={<ToggleSwitch checked={showBuilderGuide} onChange={setShowBuilderGuide} label="Toggle guided start" data-id="element-512" />} data-id="element-511" />
          </div>
        </section>

        <section aria-labelledby="settings-appearance" data-id="element-513">
          <h3 id="settings-appearance" className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-wider text-navy-400" data-id="element-514">
            Appearance
          </h3>
          <div className="card-float divide-y divide-navy-50 rounded-2xl bg-white" data-id="element-515">
            <SettingRow icon={theme === 'dark' ? MoonIcon : SunIcon} title="Dark sidebar" description="Use the deep navy sidebar. When off, the sidebar switches to a light style — the rest of the workspace stays the same." control={<ToggleSwitch checked={theme === 'dark'} onChange={() => toggleTheme()} label="Toggle dark sidebar" data-id="element-517" />} data-id="element-516" />
          </div>
        </section>

        <section aria-labelledby="settings-exports" data-id="element-518">
          <h3 id="settings-exports" className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-wider text-navy-400" data-id="element-519">
            Exports
          </h3>
          <div className="card-float divide-y divide-navy-50 rounded-2xl bg-white" data-id="element-520">
            <SettingRow icon={CloudIcon} title="Sync exports to Google Drive" description="Completed exports are delivered to your connected Google Drive folder instead of the download queue." control={<ToggleSwitch checked={driveSynced} onChange={setDriveSynced} label="Toggle Google Drive sync" data-id="element-522" />} data-id="element-521" />
          </div>
        </section>
      </motion.div>
    </div>;
}